import './Cookies.scss'

import {wrap} from '@codeurs/front'
import React from 'react'
import {useCookies} from '../../cookies/cookies'
import {CookieCategoryModel} from '../../cookies/models/CookieCategoryModel'
import {CookieTheme} from '../../cookies/views/CookieTheme'
import {PageData} from '../../types'
import {useTranslation} from '../../util/i18n'
import {slugify} from '../../util/slugify'
import {style} from '../../util/style'
import {Block, Blocks, BlockType} from '../blocks/Blocks'
import {Breadcrumbs} from '../layout/Breadcrumbs'
import {Hero} from '../layout/Hero'
import {Layout} from '../layout/Layout'
import {Theme} from '../Theme'

const styles = style('cookies', ['settings', 'categories', 'category', 'table'])

export const Cookies: React.FC<
	PageData & {
		blocks?: Array<BlockType | {type: 'internal'}>
	}
> = wrap(Layout)(({...props}) => {
	const {parents, title, hero, blocks} = props

	const processedBlocks: Array<BlockType> = blocks.map((block) => {
		if (block.type === 'cookies')
			return {
				type: 'custom',
				render: () => (
					<Block>
						<CookiesSettings />
						<CookiesCategories />
					</Block>
				)
			}
		return block
	})

	return (
		<div className={styles()}>
			<Breadcrumbs parents={parents} title={title} />
			<Hero {...hero} title={hero?.title || title} />
			<Blocks blocks={processedBlocks} container={true} />
		</div>
	)
})

const CookiesSettings: React.FC = () => {
	const {setSettingsOpen} = useCookies()
	const t = useTranslation('cookies').settings

	return (
		<div className={styles.settings()}>
			<Theme.Container>
				<Theme.H2 className={styles.settings('title')}>{t.title}</Theme.H2>
				<div className={styles.settings('text')}>
					<Theme.Wysiwyg>{t.text}</Theme.Wysiwyg>
				</div>
				<CookieTheme.Button
					onClick={() => setSettingsOpen(true)}
					className={styles.settings('button')}
					mod="outline"
				>
					{t.title}
				</CookieTheme.Button>
			</Theme.Container>
		</div>
	)
}

const CookiesCategories: React.FC = () => {
	const {settings} = useCookies()
	const t = useTranslation('cookies')
	if (!settings || settings.categories?.length === 0) return null

	return (
		<div className={styles.categories()}>
			<Theme.Container>
				<Theme.H2 className={styles.categories('title')}>{t.used}</Theme.H2>
				{settings.categories.map((category) => (
					<CookiesCategory key={category?.key} category={category} />
				))}
			</Theme.Container>
		</div>
	)
}

const CookiesCategory: React.FC<{
	category: CookieCategoryModel
}> = ({category}) => {
	const copy = category.config

	return (
		<div id={slugify(copy.title)} className={styles.category()}>
			<Theme.H4 as="h3">{copy.title}</Theme.H4>
			{copy.text && (
				<div className={styles.category('description')}>
					<Theme.Wysiwyg>{copy.text}</Theme.Wysiwyg>
				</div>
			)}
			{category.services.length > 0 && (
				<div className={styles.category('services')}>
					{category.services.map((service) => (
						<div
							id={slugify(service.config.title)}
							key={slugify(service.config.title)}
							className={styles.category('services-item')}
						>
							<Theme.H5>{service.config.title}</Theme.H5>
							<Theme.Wysiwyg>{service.config.text}</Theme.Wysiwyg>
							{!!service.config.cookies && (
								<CookiesTable cookies={service.config.cookies} />
							)}
						</div>
					))}
				</div>
			)}
		</div>
	)
}

const CookiesTable: React.FC<{cookies: Array<any>}> = ({cookies}) => {
	const t = useTranslation('cookies').table
	if (!cookies || cookies.length === 0) return null

	return (
		<table className={styles.table()}>
			<thead>
				<tr>
					<td>{t.name}</td>
					<td>{t.purpose}</td>
					<td>{t.domain}</td>
					<td>{t.duration}</td>
				</tr>
			</thead>
			<tbody>
				{cookies.map(
					(cookie) =>
						cookie &&
						cookie.name && (
							<tr>
								<td data-label={t.name}>{cookie.name}</td>
								<td data-label={t.purpose}>
									<Theme.Wysiwyg>{cookie.description}</Theme.Wysiwyg>
								</td>
								<td data-label={t.domain}>{cookie.domain}</td>
								<td data-label={t.duration}>
									<CookiesDuration
										duration={cookie.duration}
										custom={cookie.custom}
									/>
								</td>
							</tr>
						)
				)}
			</tbody>
		</table>
	)
}

const CookiesDuration: React.FC<{
	duration: 'custom' | 'persistent' | 'session'
	custom?: {
		amount: number
		unit: string
	}
}> = ({duration, custom}) => {
	const t = useTranslation('cookies').duration

	switch (duration) {
		case 'custom':
			return (
				<p>
					{custom.amount} {cookiesCustom(custom.amount, custom.unit)}
				</p>
			)
		case 'persistent':
			return <p>{t.persistent}</p>
		default:
			return <p>{t.session}</p>
	}
}

function cookiesCustom(amount, unit) {
	const t = useTranslation('cookies').duration

	switch (true) {
		case unit === 'day' && amount === '1':
			return t.day
		case unit === 'day' && amount !== '1':
			return t.days

		case unit === 'month' && amount === '1':
			return t.month
		case unit === 'month' && amount !== '1':
			return t.months

		default:
			return t.year
	}
}
